import { useCallback, useState } from 'react';
import { api } from '../../lib/api';

export type CopyState = 'idle' | 'copying' | 'success' | 'error';

/**
 * Fetch the plain-text body of a note from the backend and write it to
 * the system clipboard. Returns a state machine the caller can use to
 * drive a toast / button label.
 *
 * Flow:
 *   idle → copying → (success | error) → idle (after ~2s)
 *
 * Note: api.get already handles text/plain responses via response.text()
 * when the content-type is not application/json.
 */
export function useCopyToEmr(): {
  state: CopyState;
  copy: (noteId: string) => Promise<void>;
} {
  const [state, setState] = useState<CopyState>('idle');

  const copy = useCallback(async (noteId: string) => {
    setState('copying');
    try {
      const text = await api.get<string>(`/api/notes/${noteId}/plain-text`);
      await navigator.clipboard.writeText(text);
      setState('success');
    } catch {
      setState('error');
    } finally {
      // Reset to idle after 2s so the button is ready for another copy.
      setTimeout(() => setState('idle'), 2_000);
    }
  }, []);

  return { state, copy };
}
